import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import CustomTextInput from '@components/CustomTextInput';
import Text, { TEXT_SIZE, TEXT_COLOR } from '@components/Text';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { PasswordHint } from '../../../Authentication/PasswordHint';
import {
  formHasErrors,
  getErrorMessageId,
  getPasswordStrength,
  fieldTypes,
  checkStrengthPassword,
} from '../../../Authentication/validate';
import {
  Wrapper,
  ContentContainer,
  Header,
  InputContainer,
  HintContainer,
  StyledCTAButton,
  InputContainerAutoHeight,
} from './styles';

export class ChangePassword extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      values: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      errors: {
        oldPassword: null,
        newPassword: null,
        confirmPassword: null,
      },
      touched: {
        oldPassword: false,
        newPassword: false,
        confirmPassword: false,
      },
      submitAttempted: false,
      isTooltipShown: false,
      passwordStrength: -1,
      disableSubmit: true,
    };
  }

  computeErrors = ({ values, passwordStrength }) => {
    const newErrors = {};
    const passwordStrengthErrors = checkStrengthPassword(values.newPassword);

    _.forEach(values, (fieldValue, fieldName) => {
      const confirmField =
        fieldName === 'confirmPassword' ? values.newPassword : null;
      const options = {
        isWeak:
          fieldName === 'newPassword' && passwordStrength !== 2,
      };
      const messageId = getErrorMessageId(
        fieldTypes.password,
        fieldValue,
        confirmField,
        options,
      );

      if (
        fieldName === 'confirmPassword'
        && messageId === 'passwordsNoMatch'
        && passwordStrengthErrors.length
      ) {
        newErrors[fieldName] = null;
        return;
      }

      newErrors[fieldName] = messageId
        ? this.props.t(`Errors.${messageId}`)
        : null;
    });

    if (
      values.oldPassword &&
      values.newPassword &&
      values.oldPassword === values.newPassword &&
      !newErrors.newPassword
    ) {
      newErrors.newPassword = this.props.t('Errors.passwordSameAsOld');
    }

    return newErrors;
  };

  recomputeDisableSubmit = (nextState = null) => {
    const state = nextState || this.state;
    const { values, passwordStrength } = state;
    const errors = this.computeErrors({ values, passwordStrength });

    const hasMissingRequiredFields =
      !values.oldPassword ||
      !values.newPassword ||
      !values.confirmPassword;

    const isSameAsOld =
      values.oldPassword &&
      values.newPassword &&
      values.oldPassword === values.newPassword;

    const shouldDisable =
      hasMissingRequiredFields ||
      isSameAsOld ||
      formHasErrors(errors);

    if (shouldDisable !== this.state.disableSubmit) {
      this.setState({ disableSubmit: shouldDisable });
    }
  };

  getErrors = () => {
    const { values, passwordStrength } = this.state;
    return this.computeErrors({ values, passwordStrength });
  };

  getDisplayErrors = (allErrors) => {
    const { touched, submitAttempted } = this.state;
    const display = {
      oldPassword: null,
      newPassword: null,
      confirmPassword: null,
    };
    Object.keys(display).forEach((key) => {
      if (submitAttempted || touched[key]) {
        display[key] = allErrors[key] || null;
      }
    });
    return display;
  };

  getPasswordHint = () => {
    const { values: { newPassword }, errors } = this.state;
    if (!newPassword.length || errors.newPassword) return null;
    const errorsList = checkStrengthPassword(newPassword).map((messageId) => this.props.t(`Errors.${messageId}`));
    return (
      <HintContainer>
        <PasswordHint errors={errorsList} />
      </HintContainer>
    );
  };

  handleFocus = (event) => {
    const { name } = event.target;
    const { userHasRole } = this.props;
    const { isTooltipShown, passwordStrength } = this.state;
    if (
      userHasRole &&
      name === 'newPassword' &&
      !isTooltipShown &&
      passwordStrength !== 2
    ) {
      this.setState({ isTooltipShown: true });
    }
    this.setState((prevState) => ({
      errors: {
        ...prevState.errors,
        [name]: null,
      },
    }));
  };

  handleBlur = (event) => {
    const { name } = event.target;
    this.setState(
      (prevState) => ({
        touched: {
          ...prevState.touched,
          [name]: true,
        },
      }),
      () => {
        const allErrors = this.getErrors();
        this.setState({ errors: this.getDisplayErrors(allErrors) });
        this.recomputeDisableSubmit();
      },
    );
  };

  handleChange = (event) => {
    const { value, name } = event.target;
    const { userName } = this.props;

    const nextPasswordStrength =
      name === 'newPassword'
        ? (value.length ? getPasswordStrength(value, userName.split(' ')) : -1)
        : null;

    this.setState(
      (prevState) => ({
        passwordStrength:
          nextPasswordStrength === null ? prevState.passwordStrength : nextPasswordStrength,
        values: {
          ...prevState.values,
          [name]: value,
        },
      }),
      () => {
        this.recomputeDisableSubmit();
        if (this.state.submitAttempted || this.state.touched[name]) {
          const allErrors = this.getErrors();
          this.setState({ errors: this.getDisplayErrors(allErrors) });
        }
      },
    );
  };

  handleSubmit = () => {
    const { onSubmitPassword, onClose } = this.props;
    const { values } = this.state;
    const allErrors = this.getErrors();
    const loading = !formHasErrors(allErrors);

    if (loading) {
      onSubmitPassword(values.oldPassword, values.newPassword);
      onClose();
    }
    this.setState(
      {
        submitAttempted: true,
        errors: this.getDisplayErrors(allErrors),
        loading,
      },
      this.recomputeDisableSubmit,
    );
  };

  render() {
    const {t} = this.props
    const { values, errors, disableSubmit } = this.state;
    const commonProps = (type) => ({
      id: type,
      name: type,
      type: 'password',
      label: t(`Authentication.Password.${type}`),
      error: errors[type],
      value: values[type],
      onChange: this.handleChange,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
    });

    return (
      <Wrapper>
        <ContentContainer>
          <Header>
            <Text size={TEXT_SIZE.H1} textColor={TEXT_COLOR.DARK_BLUE}>
              {t('Authentication.Password.changePassword')}
            </Text>
          </Header>
          <InputContainer>
            <CustomTextInput {...commonProps('oldPassword')} autofocus showPasswordToggle />
          </InputContainer>
          <InputContainerAutoHeight>
            <CustomTextInput {...commonProps('newPassword')} showPasswordToggle />
            {this.getPasswordHint()}
          </InputContainerAutoHeight>
          <InputContainer>
            <CustomTextInput {...commonProps('confirmPassword')} />
          </InputContainer>
          <StyledCTAButton
            id='password-submit-button'
            expanded
            onClick={this.handleSubmit}
            disabled={disableSubmit}
          >
            {t('Actions.Instance.submit')}
          </StyledCTAButton>
        </ContentContainer>
      </Wrapper>
    );
  }
}

ChangePassword.propTypes = {
  userName: PropTypes.string,
  onSubmitPassword: PropTypes.func,
  onClose: PropTypes.func,
  userHasRole: PropTypes.bool,
};

export default compose(
    withTranslation()
)(ChangePassword);
